'use client';

import { createOrderBySku, type OrderRes } from '@/app/actions/cart';
import { queryOrderStatus } from '@/app/services/cart';
import { AlipayIcon, WechatPayIcon } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { formatPrice } from '@/lib/util';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { useQuery } from '@tanstack/react-query';
import { CheckIcon, Loader2Icon } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import Link from 'next/link';
import { QRCodeSVG as QRCode } from 'qrcode.react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

type Stage = 'confirm' | 'paying' | 'success';

export interface UpgradeInfo {
  currentPlanName: string;
  upgradeFee: number;
  currentPaidAmount: number;
  targetPrice: number;
  remainingDays: number;
  bonusDays: number;
}

export interface OrderConfirmModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  skuId: string;
  productName: string;
  skuName: string;
  price: number;
  isUpgrade?: boolean;
  upgradeInfo?: UpgradeInfo;
  onSuccess?: () => void;
  isMembershipOrder?: boolean;
  isCurrentlyMember?: boolean;
  onActivateCard?: () => void;
  skipUpgrade?: boolean;
}

const OrderConfirmModal = ({
  isOpen,
  onOpenChange,
  skuId,
  productName,
  skuName,
  price,
  isUpgrade,
  upgradeInfo,
  onSuccess,
  isMembershipOrder,
  isCurrentlyMember,
  onActivateCard,
  skipUpgrade,
}: OrderConfirmModalProps) => {
  const [stage, setStage] = useState<Stage>('confirm');
  const [payLoading, setPayLoading] = useState('');
  const [orderRes, setOrderRes] = useState<OrderRes | null>(null);
  const pollingOrderId = useRef<string | null>(null);
  const lastPayType = useRef('');

  // 轮询支付状态（客户端直连后端，避免 server action 两跳 + 静默失败）
  const { data: orderStatus } = useQuery({
    queryKey: ['order-status', pollingOrderId.current],
    queryFn: () => queryOrderStatus(pollingOrderId.current!),
    refetchInterval: stage === 'paying' ? 1500 : false,
    enabled: stage === 'paying' && !!pollingOrderId.current,
  });

  useEffect(() => {
    if (orderStatus?.status === 'success') {
      setStage('success');
    }
  }, [orderStatus]);

  // 成功后触发回调（仅在 stage 变为 success 时执行一次）
  const successHandledRef = useRef(false);
  useEffect(() => {
    if (stage !== 'success') {
      successHandledRef.current = false;
      return;
    }
    if (successHandledRef.current) return;
    successHandledRef.current = true;

    // 会员购买订单（非升级）：延迟到弹窗关闭后再触发 onSuccess（避免与全局激活提醒冲突）
    // 升级订单和非会员订单：立即触发并自动关闭
    if (!isMembershipOrder || isUpgrade) {
      onSuccess?.();
      const timer = setTimeout(() => {
        onOpenChange(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [stage]);

  // 弹窗关闭时，若支付已成功且为会员订单，触发 onSuccess
  const prevOpenRef = useRef(isOpen);
  useEffect(() => {
    if (
      prevOpenRef.current &&
      !isOpen &&
      successHandledRef.current &&
      isMembershipOrder
    ) {
      onSuccess?.();
    }
    prevOpenRef.current = isOpen;
  }, [isOpen]);

  // 重置状态
  useEffect(() => {
    if (isOpen) {
      setStage('confirm');
      setPayLoading('');
      setOrderRes(null);
      pollingOrderId.current = null;
    }
  }, [isOpen]);

  const handlePay = useCallback(
    async (payType: string) => {
      setPayLoading(payType);
      lastPayType.current = payType;
      try {
        const res = await createOrderBySku({
          sku_id: skuId,
          pay_type: payType,
          ...(skipUpgrade ? { skip_upgrade: true } : {}),
        });
        setOrderRes(res);
        pollingOrderId.current = res.order_id;

        // 零元订单直接跳到成功
        if (res.display_mode === 'zero_amount') {
          setStage('success');
        } else {
          setStage('paying');
        }
      } catch (e) {
        toast.error(
          e instanceof Error ? e.message : '创建订单失败，请稍后重试',
        );
      } finally {
        setPayLoading('');
      }
    },
    [skuId],
  );

  const handleClose = (open: boolean) => {
    if (!open && stage === 'paying') {
      // 支付中关闭时确认
      if (!window.confirm('支付尚未完成，确定要关闭吗？')) return;
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden bg-card">
        <VisuallyHidden>
          <DialogTitle>订单确认</DialogTitle>
        </VisuallyHidden>

        <AnimatePresence mode="wait">
          {stage === 'confirm' && (
            <ConfirmStage
              productName={productName}
              skuName={skuName}
              price={price}
              isUpgrade={isUpgrade}
              upgradeInfo={upgradeInfo}
              payLoading={payLoading}
              onPay={handlePay}
            />
          )}

          {stage === 'paying' && orderRes && (
            <PayingStage orderRes={orderRes} payType={lastPayType.current} />
          )}

          {stage === 'success' && (
            <SuccessStage
              isMembershipOrder={isMembershipOrder}
              isCurrentlyMember={isCurrentlyMember}
              isUpgrade={isUpgrade}
              onActivateCard={onActivateCard}
              onClose={() => onOpenChange(false)}
            />
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};

// ---- Confirm Stage ----

const ConfirmStage = ({
  productName,
  skuName,
  price,
  isUpgrade,
  upgradeInfo,
  payLoading,
  onPay,
}: {
  productName: string;
  skuName: string;
  price: number;
  isUpgrade?: boolean;
  upgradeInfo?: UpgradeInfo;
  payLoading: string;
  onPay: (payType: string) => void;
}) => {
  return (
    <motion.div
      key="confirm"
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="p-6 space-y-6"
    >
      <div>
        <h3 className="font-bold text-lg">确认订单</h3>
        <p className="text-sm text-muted-foreground mt-1">
          {isUpgrade ? '升级方案' : '订阅方案'}
        </p>
      </div>

      <div className="space-y-3 text-sm">
        <div className="flex justify-between">
          <span className="text-muted-foreground">方案</span>
          <span className="font-medium">{productName}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">套餐</span>
          <span className="font-medium">{skuName}</span>
        </div>

        {isUpgrade && upgradeInfo && (
          <div className="border-t pt-3 mt-3 space-y-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground">当前方案</span>
              <span>{upgradeInfo.currentPlanName}</span>
            </div>
            {upgradeInfo.remainingDays > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">剩余时间 *</span>
                <span>{Math.ceil(upgradeInfo.remainingDays)}天</span>
              </div>
            )}
            {upgradeInfo.targetPrice > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">目标方案价格</span>
                <span>{formatPrice(upgradeInfo.targetPrice)}</span>
              </div>
            )}
            {upgradeInfo.currentPaidAmount > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">当前方案抵扣</span>
                <span className="text-green-600">
                  -{formatPrice(upgradeInfo.currentPaidAmount)}
                </span>
              </div>
            )}
          </div>
        )}

        <div className="border-t pt-3 mt-3">
          <div className="flex justify-between items-center">
            <span className="font-medium">
              {isUpgrade ? '升级差价' : '应付金额'}
            </span>
            <span className="text-xl font-bold text-primary">
              {formatPrice(price)}
            </span>
          </div>
        </div>
      </div>

      {price === 0 ? (
        <Button
          size="lg"
          className="w-full"
          onClick={() => onPay('wx_native')}
          disabled={payLoading !== ''}
        >
          {payLoading ? (
            <Loader2Icon className="w-4 h-4 animate-spin" />
          ) : (
            '确认升级（免费）'
          )}
        </Button>
      ) : (
        <div className="space-y-3">
          <Button
            size="lg"
            onClick={() => onPay('wx_native')}
            className="bg-green-500 text-white w-full hover:bg-green-500/80"
            disabled={payLoading !== ''}
          >
            {payLoading === 'wx_native' ? (
              <Loader2Icon className="w-4 h-4 animate-spin" />
            ) : (
              <WechatPayIcon className="fill-white w-6 h-6" />
            )}
            微信支付
          </Button>
          <Button
            size="lg"
            onClick={() => onPay('alipay_pc')}
            className="bg-primary text-primary-foreground w-full"
            disabled={payLoading !== ''}
          >
            {payLoading === 'alipay_pc' ? (
              <Loader2Icon className="w-4 h-4 animate-spin" />
            ) : (
              <AlipayIcon className="fill-white w-6 h-6 dark:fill-background" />
            )}
            支付宝支付
          </Button>
        </div>
      )}

      <div className="text-xs text-muted-foreground">
        <p>1.支付成功后，如果积分或用户状态没有变化，刷新页面即可。</p>
        <p>2.遇到问题请联系客服，微信号：dangingong</p>
      </div>
    </motion.div>
  );
};

// ---- Paying Stage ----

const PayingStage = ({
  orderRes,
  payType,
}: {
  orderRes: OrderRes;
  payType: string;
}) => {
  const isWxNative = orderRes.display_mode === 'qr_code';
  const isAlipay = orderRes.display_mode === 'qr_code_url';

  return (
    <motion.div
      key="paying"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      className="p-6 flex flex-col items-center gap-y-4"
    >
      <h3 className="font-bold text-lg">扫码支付</h3>

      {isWxNative && (
        <>
          <div className="bg-white p-4 rounded-xl">
            <QRCode value={orderRes.display_content} size={200} />
          </div>
          <p className="text-sm text-muted-foreground">
            请打开手机微信，扫一扫完成支付
          </p>
        </>
      )}

      {isAlipay && (
        <>
          <div className="w-full aspect-square max-w-70 flex items-center justify-center">
            <iframe
              src={orderRes.display_content}
              className="w-50 h-51.25 border-0 rounded-xl overflow-hidden"
            />
          </div>
          <p className="text-sm text-muted-foreground">
            请使用支付宝扫描二维码完成支付
          </p>
        </>
      )}

      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2Icon className="w-4 h-4 animate-spin" />
        等待支付结果...
      </div>
    </motion.div>
  );
};

// ---- Success Stage ----

const SuccessStage = ({
  isMembershipOrder,
  isCurrentlyMember,
  isUpgrade,
  onActivateCard,
  onClose,
}: {
  isMembershipOrder?: boolean;
  isCurrentlyMember?: boolean;
  isUpgrade?: boolean;
  onActivateCard?: () => void;
  onClose: () => void;
}) => {
  const showActivateButton =
    isMembershipOrder && !isCurrentlyMember && !isUpgrade && onActivateCard;
  const showQueuedMessage =
    isMembershipOrder && isCurrentlyMember && !isUpgrade;

  return (
    <motion.div
      key="success"
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0 }}
      className="p-6 flex flex-col items-center gap-y-4 py-12"
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 200, damping: 15 }}
        className="w-16 h-16 rounded-full bg-green-500 flex items-center justify-center"
      >
        <CheckIcon className="w-8 h-8 text-white" />
      </motion.div>
      <h3 className="font-bold text-xl">支付成功</h3>

      {showActivateButton ? (
        <>
          <p className="text-sm text-muted-foreground text-center">
            会员卡已加入卡包，可立即激活使用。
          </p>
          <Button
            onClick={() => {
              onClose();
              onActivateCard();
            }}
            className="w-full max-w-xs"
          >
            立即激活
          </Button>
        </>
      ) : showQueuedMessage ? (
        <p className="text-sm text-muted-foreground text-center">
          会员卡已加入卡包，当前会员到期后可在
          <Link href="/card-pack" className="text-primary mx-1">
            我的卡包
          </Link>
          激活。
        </p>
      ) : (
        <p className="text-sm text-muted-foreground">
          方案已生效，即将自动关闭
        </p>
      )}
    </motion.div>
  );
};

export default OrderConfirmModal;
